const TOKEN_KEY = 'enshield:claim-tracking:one-time-token';

export function storeTrackingToken(token: string): void {
  const trimmed = token.trim();
  if (trimmed) sessionStorage.setItem(TOKEN_KEY, trimmed);
}

export function takeTrackingToken(): string | null {
  const token = sessionStorage.getItem(TOKEN_KEY);
  sessionStorage.removeItem(TOKEN_KEY);
  return token?.trim() || null;
}

export function tokenFromHash(hash: string): string | null {
  if (!hash) return null;
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  return params.get('token')?.trim() || null;
}

export function cleanTokenUrl(url: URL): string {
  url.searchParams.delete('token');
  const fragment = new URLSearchParams(url.hash.replace(/^#/, ''));
  fragment.delete('token');
  const hash = fragment.toString();
  return `${url.pathname}${url.search}${hash ? `#${hash}` : ''}`;
}

export function consumeTrackingTokenFromUrl(url: URL, replace: (cleanUrl: string) => void): string | null {
  const queryHadToken = url.searchParams.has('token');
  const fragmentToken = tokenFromHash(url.hash);
  if (queryHadToken) {
    sessionStorage.removeItem(TOKEN_KEY);
    replace(cleanTokenUrl(url));
    return null;
  }
  if (fragmentToken) {
    storeTrackingToken(fragmentToken);
    replace(cleanTokenUrl(url));
  }
  return takeTrackingToken();
}

/** Query tokens are rejected before storage; accepted fragments use a one-time handoff. */
export function acceptTrackingTokenFromLocation(): string | null {
  return consumeTrackingTokenFromUrl(new URL(window.location.href), (cleanUrl) => {
    window.history.replaceState(window.history.state, '', cleanUrl);
  });
}

/**
 * The token inside whatever the shopper pasted.
 *
 * The tracking field says "Paste the tracking link or token from your email"
 * and then posted the value verbatim. A full URL is 20–200 characters, so it
 * passed validation, missed the hash lookup, and the shopper was told their
 * link was "invalid or has expired" — the one thing they had done exactly as
 * instructed.
 *
 * `tokenFromHash` already existed for this shape and was only ever applied to
 * `window.location`, never to typed input.
 *
 * Returns the input unchanged when it is not a URL, so a bare token still works
 * and an unrecognised string still reaches the server to be refused there.
 */
export function tokenFromPastedValue(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  if (!/^https?:\/\//i.test(trimmed)) return trimmed;
  try {
    const url = new URL(trimmed);
    return (
      tokenFromHash(url.hash)
      ?? url.searchParams.get('token')?.trim()
      // Some mail clients rewrite the fragment away; the last path segment is
      // the remaining place a token can be.
      ?? url.pathname.split('/').filter(Boolean).pop()
      ?? trimmed
    );
  } catch {
    // Not parseable as a URL after all — hand it on unchanged rather than
    // guessing.
    return trimmed;
  }
}
